/**
 * Shared primitives for the Ocrolus surface (the "Ocrolus" view on the loan
 * drilldown Documents tab).
 *
 * The surface is a set of stacked cards rather than one long scroll: bank
 * statements + send, the system-written bank-analysis note, the account-wide
 * book browser, and a status rail. These are the pieces they share so the four
 * files don't each invent their own card chrome.
 */
import Box from "@mui/material/Box";

import Flx from "../../../../../components/layout/Flx";
import Txt from "../../../../../components/typography/Txt";
import {
  ADMIN_GRID_COL_DEF,
  ADMIN_GRID_SX,
} from "../../../../admin/shared/adminGrid";
import colors, { status as statusColors } from "../../../../../theme/colors";

/**
 * The house table look, borrowed wholesale from the admin/pricing screens
 * (small-caps faint headers, hairline row rules, no column separators, no
 * per-header menu buttons). Borderless here: these grids sit inside an
 * `OcrolusCard`, which already draws the frame — a second border and radius
 * inside it reads as a box in a box.
 *
 * The one departure is row height. The pricing tables are single-line numeric
 * rows at 36px; these carry a name over a secondary line, so they need 46.
 */
export const OCROLUS_GRID_SX = {
  ...ADMIN_GRID_SX,
  "--ag-row-height": "46px",
  ".ag-row": { ...ADMIN_GRID_SX[".ag-row"], minHeight: "46px" },
};

export const OCROLUS_GRID_ROW_H = 46;

export const OCROLUS_COL_DEF = ADMIN_GRID_COL_DEF;

/**
 * The surface's page ground — a near-white canvas so the white cards read as
 * cards rather than as one flat sheet. This is a NEUTRAL background, not a
 * brand/accent/status color (which is what the `colors.js` rule governs); if a
 * canvas of this kind spreads to other screens it belongs in `colors.js` as a
 * token rather than being retyped.
 */
export const SURFACE_BG = "#fbfcfd";

// The four derived book states from useOcrolusBook, mapped to how they read.
// "processing" is deliberately `info` (steel) not warning — results arriving by
// webhook is the normal path, not a problem.
export const STATE_STYLE = {
  none: { label: "No Ocrolus book", ...statusColors.neutral },
  empty: { label: "Book created", ...statusColors.info },
  processing: { label: "Processing", ...statusColors.info },
  ready: { label: "Analysis ready", ...statusColors.success },
};

// Book-level authenticity verdict (`signals.riskLevel`). Since the 2026-10-15
// regrade this is the WORST document's rating on Ocrolus's own scale (a LOW
// authenticity document → HIGH risk). HIGH/MEDIUM already produced a review
// Condition and an `ocrolus_signals_detected` notification server-side — the UI
// only has to surface it.
export const RISK_STYLE = {
  HIGH: statusColors.error,
  MEDIUM: statusColors.warning,
  LOW: statusColors.success,
};

// Per-document authenticity (`signals.documents[].authenticity`) — the inverse
// scale: LOW authenticity is the bad one.
export const AUTHENTICITY_STYLE = {
  LOW: statusColors.error,
  MEDIUM: statusColors.warning,
  HIGH: statusColors.success,
};

const RISK_RANK = { HIGH: 0, MEDIUM: 1, LOW: 2 };

/** HIGH → MEDIUM → LOW risk, then by name (the doc's sort order). */
export const sortAuthenticityDocs = (docs) =>
  [...(Array.isArray(docs) ? docs : [])].sort(
    (a, b) =>
      (RISK_RANK[a?.riskLevel] ?? 3) - (RISK_RANK[b?.riskLevel] ?? 3) ||
      String(a?.name || a?.docType || "").localeCompare(
        String(b?.name || b?.docType || ""),
      ),
  );

/** Ocrolus reason codes are shown as-is, lower-cased with spaces. */
export const formatReasonCode = (code) =>
  String(code || "")
    .toLowerCase()
    .replace(/_/g, " ");

/**
 * `gradingVersion == null` → the book was graded before the 2026-10-15 change:
 * its counts are the old signal tally and there is no per-document list.
 */
export const isLegacyGrading = (signals) =>
  !!signals && signals.gradingVersion == null;

/**
 * One card on the Ocrolus surface: a header strip (title, optional inline
 * subtitle, right-aligned controls) over an unpadded body, so a card can hold
 * either an AG Grid that reaches the card edges or its own padded content.
 */
export const OcrolusCard = ({
  title,
  subtitle,
  endContent,
  children,
  bodySx,
  sx,
}) => (
  <Box
    sx={{
      borderRadius: "12px",
      border: `1px solid ${colors.slate200}`,
      background: colors.white,
      overflow: "hidden",
      ...sx,
    }}
  >
    <Flx
      ac
      jb
      wrap
      g={2}
      sx={{
        px: 2,
        py: 1.5,
        borderBottom: `1px solid ${colors.slate200}`,
        minHeight: 56,
      }}
    >
      <Flx ac g={1.5} sx={{ minWidth: 0 }}>
        <Txt sx={{ fontSize: 14, fontWeight: 600, color: colors.ink }}>
          {title}
        </Txt>
        {/* A string subtitle gets the muted label treatment; anything else
            (a status Chip, say) renders as passed — Txt is a span, so a Chip
            nested inside one would be a div in a span. */}
        {typeof subtitle === "string" ? (
          <Txt sx={{ fontSize: 12, color: colors.slate500 }}>{subtitle}</Txt>
        ) : (
          subtitle
        )}
      </Flx>
      {endContent && (
        <Flx ac g={1.5} sx={{ flexShrink: 0 }}>
          {endContent}
        </Flx>
      )}
    </Flx>
    <Box sx={bodySx}>{children}</Box>
  </Box>
);

/** Centered "nothing here yet" block used by every card on this surface. */
export const OcrolusEmptyState = ({ icon: Icon, title, children, action }) => (
  <Flx column ac g={1} sx={{ px: 3, py: 5, textAlign: "center" }}>
    {Icon && <Icon sx={{ fontSize: 30, color: colors.slate300 }} />}
    <Txt sx={{ fontSize: 14, fontWeight: 600, color: colors.ink, mt: 0.5 }}>
      {title}
    </Txt>
    {children && (
      <Txt
        sx={{
          fontSize: 12.5,
          color: colors.slate500,
          maxWidth: 320,
          lineHeight: 1.5,
        }}
        component="div"
      >
        {children}
      </Txt>
    )}
    {action && <Box sx={{ mt: 1 }}>{action}</Box>}
  </Flx>
);

/**
 * Label/value row for the status rail. Txt is inline by default, so the stack
 * needs a flex column — vertical margins are ignored on a span.
 */
export const OcrolusStat = ({ label, value, hint, valueColor }) => (
  <Flx column g={0.25} sx={{ minWidth: 0 }}>
    <Txt
      sx={{
        fontSize: 10.5,
        fontWeight: 600,
        letterSpacing: 0.4,
        textTransform: "uppercase",
        color: colors.slate500,
      }}
    >
      {label}
    </Txt>
    <Txt
      sx={{
        fontSize: 15,
        fontWeight: 700,
        color: valueColor || colors.ink,
        overflowWrap: "anywhere",
      }}
    >
      {value}
    </Txt>
    {hint && <Txt sx={{ fontSize: 11, color: colors.slate500 }}>{hint}</Txt>}
  </Flx>
);
